// Central RBAC matrix — mirrors x-roles from /app/docs/openapi.yaml.
// Server is authoritative; this only shapes what the UI shows/hides.
import { useAuth } from "@/store/auth";

export const ROLES = ["admin", "service", "operator", "viewer"];

// One capability key per action. Format: "verb:section".
// Keep in sync with openapi.yaml x-roles.
const MATRIX = {
  // Users (admin-only section)
  "read:users":            ["admin"],
  "write:users":           ["admin"],
  // Clients
  "read:clients":          ["admin","service","operator","viewer"],
  "write:clients":         ["admin","service"],
  // Contacts
  "read:contacts":         ["admin","service","viewer"],
  "write:contacts":        ["admin","service"],
  // Devices
  "read:devices":          ["admin","service","operator","viewer"],
  "write:devices":         ["admin","service","operator"],
  "read:device_types":     ["admin","service","operator","viewer"],
  "write:device_types":    ["admin","service"],
  "read:device_telemetry": ["admin","service","viewer"],
  // Gateways
  "read:gateways":         ["admin","service","operator","viewer"],
  "write:gateways":        ["admin","service","operator"],
  "read:gateway_hw":       ["admin","service","operator","viewer"],
  "write:gateway_hw":      ["admin","service"],
  "read:gw_messages":      ["admin","service","viewer"],
  // Callbacks
  "read:callbacks":        ["admin","service","viewer"],
  "write:callbacks":       ["admin","service"],
  "test:callbacks":        ["admin","service"],
  // Envelope
  "read:envelope":         ["admin","service","viewer"],
  "write:envelope":        ["admin","service"],
  // MQTT / Outputs / Pipelines
  "read:mqtt":             ["admin","service","viewer"],
  "write:mqtt":            ["admin","service"],
  "read:outputs":          ["admin","service","viewer"],
  "write:outputs":         ["admin","service"],
  "read:pipelines":        ["admin","service","viewer"],
  "write:pipelines":       ["admin","service"],
  // Telemetry / System
  "read:telemetry":        ["admin","service","viewer"],
  "read:pool_stats":       ["admin","service"],
  // Audit log — admin & service only
  "read:audit":            ["admin","service"],
};

// Direct role check (used outside React components / stores).
export function can(role, capability) {
  const allowed = MATRIX[capability];
  return !!(role && allowed && allowed.includes(role));
}

// Decode a JWT payload without verification (UI-shaping only!).
export function decodeJwt(token) {
  if (!token) return null;
  try {
    const b = token.split(".")[1];
    // base64url → base64
    const json = atob(b.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch { return null; }
}

/**
 * React hook — reactive capability check bound to the current user.
 * Usage: const canEditDevices = useCan("write:devices");
 *        {canEditDevices && <button>New device</button>}
 *
 * For operator scope checks, pass a client_id as the 2nd arg — this returns
 * true only if the operator has that client_id in their JWT scope (or the
 * role has global write like admin/service).
 */
export function useCan(capability, clientId = null) {
  const role = useAuth((s) => s.role);
  const clientIds = useAuth((s) => s.clientIds);
  if (!can(role, capability)) return false;
  if (role === "operator" && clientId != null) {
    // Operator can only act on their assigned clients.
    return (clientIds || []).map(String).includes(String(clientId));
  }
  return true;
}

// Non-reactive helper — takes an auth snapshot object.
export function canFor({ role, clientIds }, capability, clientId = null) {
  if (!can(role, capability)) return false;
  if (role === "operator" && clientId != null) {
    return (clientIds || []).map(String).includes(String(clientId));
  }
  return true;
}

/**
 * Returns the list of client_ids the current session is scoped to.
 * - admin / service / viewer → null (means "no filter, all allowed").
 * - operator → array of allowed client_ids (may be []).
 */
export function useScopedClientIds() {
  const role = useAuth((s) => s.role);
  const clientIds = useAuth((s) => s.clientIds);
  if (role === "operator") return (clientIds || []).map(String);
  return null;
}
